// Wishlist domain vocabulary (W53.1 D-6 / ADR-0178).
//
// The status labels used to live in **five** hand-maintained maps — one per
// surface that happens to show a status — and a sixth place printed the raw
// enum straight into the UI:
//
//   components/wish/KanbanColumn.tsx            STATUS_META.labelZh
//   routes/wishes/components/MobileCollapsible…  MOBILE_STATUS_LABELS
//   routes/WishDetail.tsx                       STATUS_LABELS
//   components/wish/KanbanBoard.tsx             KANBAN_LABEL
//   components/wish/AsideStatsPanel.tsx         inline stat tile
//   components/diary/LinkedWishPanel.tsx        `狀態 {wish.status}`  ← leaked
//
// Six copies is one rename away from six different answers, and the diary panel
// proved it: `lib/i18n/dashboard.ts` exists precisely so that "raw English enum
// in a zh-Hant UI" cannot recur — and it recurred in the next file that nobody
// connected to that rule. Guard G-2 (no file renders `wish.status` in JSX) and
// G-3 (「待完成」 exists in exactly one file) keep it that way.
//
// Only the **display layer** changed. The DB enum, the OpenAPI contract and the
// generated TS types all still say `pending` — renaming the value would cost a
// migration and a contract break for nothing a user would ever see.

import type { WishStatus } from "@/lib/api/wishlist";

/**
 * The four statuses, named once.
 *
 * `pending` reads 「計劃中」 rather than 「待完成」: the kanban column is not a
 * backlog of things someone else is waiting on, it is the column for wishes the
 * pair has agreed to do. The other three keep their existing wording.
 */
export const WISH_STATUS_LABEL: Record<WishStatus, string> = {
  pending: "計劃中",
  in_progress: "進行中",
  completed: "已完成",
  abandoned: "已放棄",
};

/**
 * Kanban column order — the one place the *sequence* lives.
 *
 * It was duplicated as a bare array in three files (`KanbanBoard`,
 * `MobileCollapsibleSections`, and the board's own type-level tuple). Ordering is
 * not a label, so it does not belong in `WISH_STATUS_LABEL`, but it did not
 * need a third copy either.
 */
export const WISH_STATUS_ORDER = [
  "pending",
  "in_progress",
  "completed",
  "abandoned",
] as const satisfies readonly WishStatus[];

/** Display name for a status. Never render the raw enum in JSX (guard G-2). */
export function wishStatusLabel(status: WishStatus): string {
  return WISH_STATUS_LABEL[status];
}
